import { useSyncExternalStore } from 'react'

/// Instalação do app na tela inicial do celular.
///
/// O Android (Chrome, Edge, Samsung) avisa uma única vez, logo no carregamento,
/// que o app pode ser instalado — o evento `beforeinstallprompt`. Quem não estiver
/// escutando nesse instante perde o aviso até a próxima visita. Por isso a escuta
/// fica aqui, no nível do módulo, e começa quando o app carrega, antes de qualquer
/// tela existir.
///
/// O iPhone não tem esse evento: lá, instalar é sempre pelo menu Compartilhar do
/// Safari, e o app só consegue explicar o caminho.

interface EventoInstalacao extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let pendente: EventoInstalacao | null = null
let instaladoAgora = false
const ouvintes = new Set<() => void>()
const avisar = () => ouvintes.forEach((f) => f())

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Guarda o aviso para o botão do app usar, em vez de o navegador decidir quando
    // mostrar o dele.
    e.preventDefault()
    pendente = e as EventoInstalacao
    avisar()
  })
  window.addEventListener('appinstalled', () => {
    pendente = null
    instaladoAgora = true
    avisar()
  })
}

function assinar(f: () => void) {
  ouvintes.add(f)
  return () => ouvintes.delete(f)
}

/// Aberto pelo ícone da tela inicial, e não pelo navegador.
export function rodandoInstalado(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

export type Plataforma = 'ios' | 'android' | 'outro'

export function plataforma(): Plataforma {
  const ua = navigator.userAgent
  // O iPad se apresenta como Mac desde o iPadOS 13; o toque denuncia.
  if (/iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) {
    return 'ios'
  }
  if (/android/i.test(ua)) return 'android'
  return 'outro'
}

export function useInstalacao() {
  const evento = useSyncExternalStore(assinar, () => pendente)
  const acabouDeInstalar = useSyncExternalStore(assinar, () => instaladoAgora)

  async function instalar(): Promise<boolean> {
    const e = pendente
    if (!e) return false
    await e.prompt()
    const { outcome } = await e.userChoice
    // O aviso só pode ser usado uma vez.
    pendente = null
    avisar()
    return outcome === 'accepted'
  }

  return {
    podeInstalar: evento !== null,
    instalado: acabouDeInstalar || rodandoInstalado(),
    plataforma: plataforma(),
    instalar,
  }
}
